import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Primitive } from '@amzn/swo-unified-dashboard-service-lambda-js-client/lib/smswounifieddashboardservicelambda';
import { WebsiteViewService } from '../../model/WebsiteViewService';
import { PrimitiveWorkflowStatus } from '../website-view/constants';

export type WebsiteRunStatus = 'RUNNING' | 'DONE';

/** Shared with `useTimelineData` so react-query serves the badge and the timeline from one request. */
export const WEBSITE_EVENTS_QUERY_KEY = 'timeline';

const startedAt = (primitive: Primitive): number => Number(primitive.startTime ?? 0);

/** `endTime` is unset until an END event lands, so its absence is in-flight rather than malformed. */
const isStillRunning = (primitive: Primitive): boolean => {
  const state = primitive.metadata?.['state'];
  if (
    typeof state === 'string' &&
    state.toLowerCase() === PrimitiveWorkflowStatus.RUNNING.toLowerCase()
  ) {
    return true;
  }
  const endTime = Number(primitive.endTime);
  return !Number.isFinite(endTime) || endTime <= 0;
};

/**
 * One trace is one cycle, and only the newest counts: an older cycle abandoned half way through would
 * otherwise pin the badge to RUNNING for good. Newest by start time, since the search order is not sorted.
 * Null rather than DONE with no primitives, which would assert a cycle finished for an unindexed website.
 */
export const websiteRunStatus = (primitives: Primitive[] | undefined): WebsiteRunStatus | null => {
  const traced = (primitives ?? []).filter((primitive) => !!primitive.traceId);
  if (traced.length === 0) {
    return null;
  }
  const newest = traced.reduce((latest, candidate) =>
    startedAt(candidate) > startedAt(latest) ? candidate : latest
  );
  const latestCycle = traced.filter((primitive) => primitive.traceId === newest.traceId);
  return latestCycle.some(isStillRunning) ? 'RUNNING' : 'DONE';
};

/**
 * Options must match `useTimelineData`'s: two observers of one key disagreeing on staleness would refetch
 * against each other.
 */
export const useWebsiteRunStatus = (domain: string | undefined) => {
  const { data, isLoading, error } = useQuery({
    queryKey: [WEBSITE_EVENTS_QUERY_KEY, domain],
    queryFn: () => new WebsiteViewService().getWebsiteEvents(domain || ''),
    enabled: !!domain,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  return {
    status: useMemo(() => websiteRunStatus(data?.primitives), [data]),
    isLoading,
    error,
  };
};
